import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import HorizontalSplitIcon from '@mui/icons-material/HorizontalSplit';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import { apiget, apipost, apiput } from '../../service/api';
import { CustomTabPanel, a11yProps } from '../../components/CustomTabPanel';
import {
  BANKRATE_STATUSES,
  statusColor,
  fmtCurrency,
} from './statusConfig';
import EditBankrateLead from './Edit';
import ConvertToLoanModal from './ConvertToLoanModal';
import BankrateLeadActivityTimeline from './timeline/BankrateLeadActivityTimeline';
import BankrateLeadTracking from './LeadTracking';

const LAYOUT_STORAGE_KEY = 'bankrateLeadLayout';

const Field = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">{value ?? '—'}</Typography>
  </Grid>
);

const BankrateLeadView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openConvert, setOpenConvert] = useState(false);
  const [userAction, setUserAction] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [inviting, setInviting] = useState(false);
  const [layout, setLayout] = useState(() => {
    if (typeof window === 'undefined') return 'right';
    return localStorage.getItem(LAYOUT_STORAGE_KEY) || 'right';
  });

  const handleTabChange = (_event, newValue) => setTabValue(newValue);

  const handleLayoutChange = (_event, value) => {
    if (!value) return;
    setLayout(value);
    try {
      localStorage.setItem(LAYOUT_STORAGE_KEY, value);
    } catch (err) {
      // Ignore write errors (private mode, quota, etc.)
    }
  };

  const fetchLead = async () => {
    setLoading(true);
    const result = await apiget(`bankrate-lead/view/${id}`);
    if (result?.status === 200) {
      const doc = result.data?.lead || result.data?.result || result.data;
      setLead(doc);
      setNotesDraft(doc?.notes || '');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, userAction]);

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    if (!newStatus || newStatus === lead?.status) return;
    setSavingStatus(true);
    const result = await apiput(`bankrate-lead/edit/${id}`, { status: newStatus });
    setSavingStatus(false);
    if (result?.status === 200) {
      toast.success(`Status changed to ${newStatus}`);
      fetchLead();
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    const result = await apiput(`bankrate-lead/edit/${id}`, { notes: notesDraft });
    setSavingNotes(false);
    if (result?.status === 200) {
      toast.success('Notes saved');
      fetchLead();
    }
  };

  const handleInvite = async () => {
    if (!lead) return;
    if (!lead.email || !lead.phone) {
      toast.error('Borrower email and phone are required to invite.');
      return;
    }
    setInviting(true);
    try {
      const payload = {
        firstName: lead.firstName,
        lastName: lead.lastName,
        phoneNumber: lead.phone,
        emailAddress: lead.email,
        loanAmount: lead.loanAmount,
        purchasePriceRange: lead.purchasePriceRange,
        subjectPropertyZip: lead.zipCode,
        userId: localStorage.getItem('user_id'),
      };
      const result = await apipost('loan/invite', payload);
      if (result?.status === 201) {
        toast.success('Borrower invite emailed successfully!');
        setUserAction(result);
      } else {
        toast.error('Failed to email borrower invite.');
      }
    } catch (err) {
      toast.error('Failed to email borrower invite.');
    } finally {
      setInviting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!lead) {
    return (
      <Container maxWidth="xl">
        <Typography variant="h6" sx={{ mt: 4 }}>
          Bankrate lead not found.
        </Typography>
        <Button onClick={() => navigate('/dashboard/bankrate-lead')} sx={{ mt: 2 }}>
          Back to list
        </Button>
      </Container>
    );
  }

  const name =
    lead.fullName ||
    [lead.firstName, lead.lastName].filter(Boolean).join(' ') ||
    '—';
  const isPurchase = lead.loanType === 'Home Purchase';
  const isRefi = lead.loanType === 'Home Refinance';
  const isConverted = lead.status === 'Converted';

  const assignedLabel = (() => {
    const a = lead.assignedTo;
    if (!a) return '—';
    if (typeof a === 'string') return a;
    return (
      [a.firstName, a.lastName].filter(Boolean).join(' ') ||
      a.emailAddress ||
      a._id ||
      '—'
    );
  })();

  return (
    <Container maxWidth="xl">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mt={3}
        mb={2}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard/bankrate-lead')}
          >
            Back
          </Button>
          <Typography variant="h4">{name}</Typography>
          <Chip
            size="small"
            label={lead.status || 'New'}
            color={statusColor(lead.status)}
            variant="outlined"
          />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            value={layout}
            exclusive
            onChange={handleLayoutChange}
            size="small"
            aria-label="Timeline layout"
          >
            <ToggleButton value="right" aria-label="Timeline on right">
              <Tooltip title="Timeline on right">
                <VerticalSplitIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="below" aria-label="Timeline below">
              <Tooltip title="Timeline below">
                <HorizontalSplitIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setOpenEdit(true)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<MailOutlineIcon />}
            disabled={inviting || isConverted}
            onClick={handleInvite}
          >
            {inviting ? 'Inviting…' : 'Invite To Loan'}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<SwapHorizIcon />}
            disabled={isConverted}
            onClick={() => setOpenConvert(true)}
          >
            {isConverted ? 'Converted' : 'Convert to Loan'}
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={layout === 'right' ? 8 : 12}>
      <Card sx={{ p: 3, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">
                Lead ID
              </Typography>
              <Typography variant="body1">{lead.leadId || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Source
              </Typography>
              <Typography variant="body1">{lead.ingestSource || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body1">
                {lead.createdOn ? dayjs(lead.createdOn).format('MM/DD/YYYY h:mm A') : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Assigned
              </Typography>
              <Typography variant="body1">{assignedLabel}</Typography>
            </Box>
            {isConverted && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Encompass Loan
                </Typography>
                <Typography variant="body1">
                  {lead.convertedLoanId || '—'}
                </Typography>
              </Box>
            )}
          </Stack>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={lead.status || 'New'}
              onChange={handleStatusChange}
              disabled={savingStatus || isConverted}
            >
              {BANKRATE_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Card>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="bankrate lead tabs">
          <Tab label="Lead Details" {...a11yProps(0)} />
          <Tab label="Lead Tracking" {...a11yProps(1)} />
        </Tabs>
      </Box>

      <CustomTabPanel value={tabValue} index={0}>
      <Card sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Borrower
        </Typography>
        <Grid container spacing={2}>
          <Field label="First Name" value={lead.firstName} />
          <Field label="Last Name" value={lead.lastName} />
          <Field label="Email" value={lead.email} />
          <Field label="Phone" value={lead.phone} />
          <Field label="Employment" value={lead.employmentStatus} />
          <Field label="Military" value={lead.isMilitaryMember ? 'Yes' : lead.isMilitaryMember === false ? 'No' : '—'} />
          <Field label="Credit Profile" value={lead.creditProfileDescription} />
          <Field label="Credit Score" value={lead.creditProfileAmount} />
          <Field label="Gross Annual Income" value={fmtCurrency(lead.grossAnnualIncomeAmount)} />
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Property / Loan
        </Typography>
        <Grid container spacing={2}>
          <Field label="Loan Type" value={lead.loanType} />
          <Field label="Loan Amount" value={fmtCurrency(lead.loanAmount)} />
          <Field label="Property Type" value={lead.propertyType} />
          <Field label="Property Use" value={lead.propertyUse} />
          <Field label="City" value={lead.propertyCity} />
          <Field label="State" value={lead.propertyState} />
          <Field label="ZIP" value={lead.zipCode} />
        </Grid>

        {isPurchase && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Purchase Details
            </Typography>
            <Grid container spacing={2}>
              <Field label="Purchase Price" value={fmtCurrency(lead.purchasePriceAmount)} />
              <Field label="Down Payment" value={fmtCurrency(lead.downPaymentAmount)} />
              <Field label="Down Payment %" value={lead.downPaymentPercent} />
              <Field label="Rate Type Desired" value={lead.rateTypeDesired} />
              <Field label="First-Time Buyer" value={lead.isFirstPropertyPurchase ? 'Yes' : lead.isFirstPropertyPurchase === false ? 'No' : '—'} />
              <Field label="Working with Realtor" value={lead.isWorkingWithRealtor ? 'Yes' : lead.isWorkingWithRealtor === false ? 'No' : '—'} />
              <Field label="Purchase Situation" value={lead.propertyPurchaseSituation} />
            </Grid>
          </>
        )}

        {isRefi && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Refinance Details
            </Typography>
            <Grid container spacing={2}>
              <Field label="Estimated Value" value={fmtCurrency(lead.propertyEstimatedValueAmount)} />
              <Field label="1st Mortgage Balance" value={fmtCurrency(lead.firstMortgageBalanceAmount)} />
              <Field label="1st Mortgage Rate" value={lead.firstMortgageRate} />
              <Field label="Existing Rate Type" value={lead.existingRateType} />
              <Field label="Has 2nd Mortgage" value={lead.hasSecondMortgage ? 'Yes' : lead.hasSecondMortgage === false ? 'No' : '—'} />
              <Field label="Cash Out" value={fmtCurrency(lead.additionalCashAmount)} />
              <Field label="Monthly Income" value={fmtCurrency(lead.averageMonthlyIncomeAmount)} />
              <Field label="Monthly Expenses" value={fmtCurrency(lead.averageMonthlyExpensesAmount)} />
              <Field label="Late Payments" value={lead.hasLatePayments ? 'Yes' : lead.hasLatePayments === false ? 'No' : '—'} />
              <Field label="Has FHA Loan" value={lead.hasFHALoan ? 'Yes' : lead.hasFHALoan === false ? 'No' : '—'} />
              <Field label="Year Home Purchased" value={lead.yearHomePurchased || lead.housePurchaseYear} />
            </Grid>
          </>
        )}

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Bankrate Metadata
        </Typography>
        <Grid container spacing={2}>
          <Field label="Campaign" value={lead.leadSourceCampaign} />
          <Field label="Lead Cost" value={fmtCurrency(lead.leadcost)} />
          <Field label="Product" value={lead.productDesc} />
          <Field label="Product Rate" value={lead.productRate} />
          <Field label="Product APR" value={lead.productAPR} />
          <Field label="Points" value={lead.points} />
        </Grid>
      </Card>

      <Card sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Notes
        </Typography>
        <TextField
          multiline
          fullWidth
          minRows={3}
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          placeholder="Add internal notes about this lead…"
        />
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button
            variant="contained"
            onClick={handleSaveNotes}
            disabled={savingNotes || notesDraft === (lead.notes || '')}
          >
            {savingNotes ? 'Saving…' : 'Save Notes'}
          </Button>
        </Stack>
      </Card>

      <Accordion sx={{ mb: 4 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Raw Bankrate Payload (debug)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            component="pre"
            sx={{
              fontSize: 12,
              bgcolor: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: 400,
            }}
          >
            {JSON.stringify(lead.rawPayload || {}, null, 2)}
          </Box>
        </AccordionDetails>
      </Accordion>
      </CustomTabPanel>

      <CustomTabPanel value={tabValue} index={1}>
        <BankrateLeadTracking data={lead} setUserAction={setUserAction} />
      </CustomTabPanel>
        </Grid>

        <Grid
          item
          xs={12}
          md={layout === 'right' ? 4 : 12}
          sx={{ alignSelf: 'flex-start' }}
        >
          <BankrateLeadActivityTimeline
            bankrateLeadId={lead._id}
            borrowerName={name}
            borrowerEmail={lead.email}
            borrowerPhone={lead.phone}
            setUserAction={setUserAction}
          />
        </Grid>
      </Grid>

      <EditBankrateLead
        open={openEdit}
        handleClose={() => setOpenEdit(false)}
        lead={lead}
        setUserAction={setUserAction}
      />
      <ConvertToLoanModal
        open={openConvert}
        handleClose={() => setOpenConvert(false)}
        lead={lead}
        onConverted={() => {
          setOpenConvert(false);
          fetchLead();
        }}
      />
    </Container>
  );
};

export default BankrateLeadView;
